import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatErr } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=user
  const [bootstrapped, setBootstrapped] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("cbc_token");
    if (!token) {
      setUser(false);
      setBootstrapped(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("cbc_token");
      setUser(false);
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 2FA path
      if (data.step === "2fa") {
        return { ok: true, twoFactor: true, challenge_id: data.challenge_id, email_masked: data.email_masked };
      }
      // Legacy single-step path (kept for safety)
      if (data.access_token) localStorage.setItem("cbc_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const verify2FA = async (challenge_id, code) => {
    try {
      const { data } = await api.post("/auth/2fa-verify", { challenge_id, code });
      if (data.access_token) localStorage.setItem("cbc_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const resend2FA = async (challenge_id) => {
    try {
      await api.post("/auth/2fa-resend", { challenge_id });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) localStorage.setItem("cbc_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cbc_token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, bootstrapped, login, verify2FA, resend2FA, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
